import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Mail, Phone, MessageSquare, MapPin, HelpCircle, ChevronDown, ChevronRight, Shield, FileText, RefreshCw, LifeBuoy, Building2 } from 'lucide-react';
import DesktopHeader from '../components/DesktopHeader';

const PageShell = ({ title, subtitle, icon: Icon, children }) => {
  const nav = useNavigate();
  return (
    <div className="min-h-screen bg-neutral-50">
      <DesktopHeader />
      <div className="max-w-[900px] mx-auto px-5 md:px-6 pt-4 md:pt-8 pb-16">
        <button onClick={() => nav(-1)} className="text-sm text-neutral-500 hover:text-neutral-900 inline-flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="bg-white border border-neutral-200 rounded-2xl p-6 md:p-10">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-[#2E7D32]" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-neutral-900">{title}</h1>
              {subtitle && <p className="mt-1 text-sm text-neutral-600">{subtitle}</p>}
              <p className="mt-2 text-xs text-neutral-400">Last updated: 19 July 2026</p>
            </div>
          </div>

          <div className="mt-8 space-y-6 text-[15px] leading-relaxed text-neutral-700">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

const Section = ({ n, title, children }) => (
  <section>
    <h2 className="font-bold text-neutral-900 text-lg">{n ? `${n}. ` : ''}{title}</h2>
    <div className="mt-2 space-y-2">{children}</div>
  </section>
);

// -------------------- TERMS --------------------
export const Terms = () => (
  <PageShell title="Terms & Conditions" subtitle="The agreement between you and Clean24." icon={FileText}>
    <p>Welcome to Clean24. By accessing or booking any service through our platform, you agree to be bound by these Terms & Conditions. Please read them carefully.</p>

    <Section n="1" title="Definitions">
      <p>“Clean24”, “we”, “us”, or “our” refers to Clean24 Services, operating in Thrissur and Ernakulam, Kerala. “Customer” means any person who books a service through our website or mobile app. “Cleaner” refers to independent, verified professionals who deliver services on our platform.</p>
    </Section>

    <Section n="2" title="Bookings & Payments">
      <p>All bookings are subject to availability of cleaners in your area. Displayed prices are inclusive of applicable taxes unless stated otherwise. Payment can be made via Cash or UPI after the service is completed, unless a pre-paid coupon is applied.</p>
    </Section>

    <Section n="3" title="Cancellations">
      <p>You may cancel a booking up to 60 minutes before the scheduled time free of charge. Cancellations made within 60 minutes of the slot may attract a small cancellation fee (₹49) to compensate the assigned cleaner.</p>
    </Section>

    <Section n="4" title="Service Quality Guarantee">
      <p>If you are not satisfied with a Clean24 service, please raise a request via the app within 24 hours. We will either re-clean the affected area free of charge or refund the service amount at our sole discretion.</p>
    </Section>

    <Section n="5" title="Customer Responsibilities">
      <p>Customers must provide safe access to the service location and disclose any hazards. Valuables should be secured. Clean24 is not responsible for pre-existing damage or wear.</p>
    </Section>

    <Section n="6" title="Limitation of Liability">
      <p>Clean24 acts as a technology platform connecting customers with independent cleaners. Our maximum liability for any claim is limited to the amount paid for the specific service in question.</p>
    </Section>

    <Section n="7" title="Modifications">
      <p>We may update these Terms from time to time. Continued use of the platform after changes are posted constitutes acceptance of the revised Terms.</p>
    </Section>

    <Section n="8" title="Governing Law">
      <p>These Terms are governed by the laws of India. Any disputes shall be subject to the exclusive jurisdiction of the courts of Ernakulam, Kerala.</p>
    </Section>
  </PageShell>
);

// -------------------- PRIVACY --------------------
export const Privacy = () => (
  <PageShell title="Privacy Policy" subtitle="How we collect, use, and protect your data." icon={Shield}>
    <p>Your privacy matters to us. This policy explains what information we collect, why, and how we keep it safe.</p>

    <Section n="1" title="Information we collect">
      <ul className="list-disc pl-5 space-y-1">
        <li>Account details: name, email, phone number, and profile photo (if provided).</li>
        <li>Service address(es) you save for bookings and live-tracking.</li>
        <li>Booking history, payment mode used, ratings & reviews you submit.</li>
        <li>Device and usage data (browser type, IP, referring URL) for security and analytics.</li>
      </ul>
    </Section>

    <Section n="2" title="How we use your information">
      <ul className="list-disc pl-5 space-y-1">
        <li>To fulfil your bookings and assign the nearest verified cleaner.</li>
        <li>To send booking confirmations, reminders, and service updates.</li>
        <li>To improve our services, prevent fraud, and ensure platform safety.</li>
        <li>To share promotional offers — you can opt out any time.</li>
      </ul>
    </Section>

    <Section n="3" title="Sharing of information">
      <p>We share only the minimum information required (name, phone, address) with the assigned cleaner. We never sell your data. Aggregated, non-identifiable data may be used for research.</p>
    </Section>

    <Section n="4" title="Data security">
      <p>We use industry-standard encryption in transit (TLS) and at rest. Passwords are stored using one-way cryptographic hashes.</p>
    </Section>

    <Section n="5" title="Your rights">
      <p>You may request access, correction, or deletion of your personal data by emailing <a href="mailto:privacy@clean24.in" className="text-[#2E7D32] font-semibold hover:underline">privacy@clean24.in</a>. We honour verified requests within 30 days.</p>
    </Section>

    <Section n="6" title="Cookies">
      <p>We use cookies to keep you signed in and remember your preferences. You can disable cookies in your browser settings; some features may not work optimally.</p>
    </Section>

    <Section n="7" title="Contact">
      <p>Grievance Officer: <b>Riya Menon</b>, Marine Drive, Ernakulam — 682031. Email: <a href="mailto:privacy@clean24.in" className="text-[#2E7D32] font-semibold hover:underline">privacy@clean24.in</a></p>
    </Section>
  </PageShell>
);

// -------------------- REFUND --------------------
export const Refunds = () => (
  <PageShell title="Refund Policy" subtitle="Simple, fair refunds — no arguments." icon={RefreshCw}>
    <p>We stand behind every Clean24 booking with our 100% satisfaction guarantee. Here is how our refund policy works.</p>

    <Section n="1" title="Eligibility for a refund">
      <ul className="list-disc pl-5 space-y-1">
        <li>Cleaner did not arrive within 30 minutes of the scheduled slot (without prior communication).</li>
        <li>Service was incomplete or of unsatisfactory quality.</li>
        <li>Cancellation initiated by Clean24 due to unavailability of a cleaner in your area.</li>
      </ul>
    </Section>

    <Section n="2" title="Refund options">
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Re-service:</b> We will re-do the service free of charge, subject to availability.</li>
        <li><b>Clean24 Coins:</b> Instant credit to your wallet, usable for future bookings (1 Coin = ₹1).</li>
        <li><b>Bank refund:</b> Refund to the original payment method within 5–7 working days.</li>
      </ul>
    </Section>

    <Section n="3" title="How to request a refund">
      <p>Raise a request within 24 hours of service completion via the in-app <b>Help & Support</b> option or email <a href="mailto:refunds@clean24.in" className="text-[#2E7D32] font-semibold hover:underline">refunds@clean24.in</a> with your booking ID. Our team will respond within 12 business hours.</p>
    </Section>

    <Section n="4" title="Non-refundable items">
      <ul className="list-disc pl-5 space-y-1">
        <li>Bookings cancelled within 60 minutes of the scheduled slot (a ₹49 fee applies).</li>
        <li>Consumables or add-on materials specifically purchased for your booking.</li>
      </ul>
    </Section>

    <Section n="5" title="Escalations">
      <p>If your refund request is not resolved to your satisfaction, please write to our Grievance Officer at <a href="mailto:grievance@clean24.in" className="text-[#2E7D32] font-semibold hover:underline">grievance@clean24.in</a>. We aim to resolve all escalations within 7 working days.</p>
    </Section>
  </PageShell>
);

// -------------------- SUPPORT --------------------
export const Support = () => {
  const nav = useNavigate();
  const [openFaq, setOpenFaq] = React.useState(0);

  const faqs = [
    { q: 'How do I book a cleaning service?', a: 'Sign in, choose a service category from the home page, pick a specific service, add it to cart, then select a date and time slot. Booking takes under 60 seconds.' },
    { q: 'Are Clean24 cleaners verified?', a: 'Yes. Every cleaner undergoes background checks, in-person interviews, skill assessments, and mandatory training before joining the platform.' },
    { q: 'What payment methods do you accept?', a: 'We accept Cash on service and UPI (paid after service completion). Pre-payment is only required when redeeming a promo code that mandates it.' },
    { q: 'Can I reschedule a booking?', a: 'Yes — up to 60 minutes before the slot, free of charge. Go to My Bookings → select the booking → Reschedule.' },
    { q: 'What if the cleaner does not show up?', a: 'If your cleaner is more than 30 minutes late without notice, you get a full refund plus ₹100 Clean24 Coins as compensation.' },
    { q: 'How do I become a Clean24 cleaner?', a: 'Visit the Cleaner Login page and sign up. Our team will contact you within 48 hours to schedule verification and training.' },
  ];

  return (
    <PageShell title="Help & Support" subtitle="Get in touch or find answers to common questions." icon={LifeBuoy}>
      {/* Contact cards */}
      <div className="grid md:grid-cols-3 gap-3 md:gap-4">
        <ContactCard icon={Phone} label="Call us" value="+91 90000 12345" href="tel:+919000012345" />
        <ContactCard icon={Mail} label="Email us" value="support@clean24.in" href="mailto:support@clean24.in" />
        <ContactCard icon={MessageSquare} label="Live chat" value="9 AM – 9 PM daily" href="#" />
      </div>

      <div className="bg-[#E8F5E9] border border-[#C8E6C9] rounded-xl p-4 flex items-start gap-3">
        <MapPin className="w-5 h-5 text-[#2E7D32] flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-bold text-sm">Head office</p>
          <p className="text-sm text-neutral-700">Clean24 Services, Marine Drive, Ernakulam — 682031, Kerala, India.</p>
          <p className="text-xs text-neutral-600 mt-1">Currently operating in <b>Thrissur</b> and <b>Ernakulam</b>.</p>
        </div>
      </div>

      {/* FAQs */}
      <Section title="Frequently Asked Questions">
        <div className="space-y-2 mt-1">
          {faqs.map((f, i) => (
            <div key={i} className="border border-neutral-200 rounded-lg overflow-hidden bg-white">
              <button onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-neutral-50 transition-colors">
                <span className="font-semibold text-sm text-neutral-900">{f.q}</span>
                <ChevronDown className={`w-4 h-4 text-neutral-500 transition-transform flex-shrink-0 ${openFaq === i ? 'rotate-180' : ''}`} />
              </button>
              {openFaq === i && (
                <div className="px-4 pb-3 text-sm text-neutral-600 border-t border-neutral-100 pt-2">
                  {f.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </Section>

      <Section title="Still need help?">
        <p>Our team responds to emails within 12 business hours. For urgent issues, please call the support line above.</p>
        <div className="mt-3 flex gap-2 flex-wrap">
          <a href="mailto:support@clean24.in" className="inline-flex items-center gap-2 bg-[#2E7D32] hover:bg-[#276D2A] text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
            <Mail className="w-4 h-4" /> Email support
          </a>
          <button onClick={() => nav('/bookings')} className="inline-flex items-center gap-2 bg-white border border-neutral-200 hover:bg-neutral-50 text-sm font-semibold px-4 py-2 rounded-lg transition-colors">
            View my bookings <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </Section>
    </PageShell>
  );
};

const ContactCard = ({ icon: Icon, label, value, href }) => (
  <a href={href} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center gap-3 hover:border-[#2E7D32] hover:shadow-sm transition-all">
    <div className="w-10 h-10 rounded-full bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
      <Icon className="w-4 h-4 text-[#2E7D32]" />
    </div>
    <div className="min-w-0">
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="font-semibold text-sm truncate">{value}</p>
    </div>
  </a>
);

// -------------------- ABOUT --------------------
export const About = () => (
  <PageShell title="About Clean24" subtitle="Sparkling clean, on demand — since 2024." icon={Building2}>
    <p>Clean24 was born out of a simple frustration: booking a reliable, verified cleaner in Kerala should be as easy as ordering food. We started in <b>Ernakulam</b> in 2024 and now operate across <b>Thrissur</b> and <b>Ernakulam</b>, with plans to expand to Kozhikode and Thiruvananthapuram in 2026.</p>

    <Section title="Our mission">
      <p>Provide households, offices, and businesses with on-demand, transparent, and guaranteed cleaning services — while creating dignified, well-paid work opportunities for cleaning professionals.</p>
    </Section>

    <Section title="Why Clean24">
      <ul className="list-disc pl-5 space-y-1">
        <li><b>Verified professionals</b> — background-checked, trained, and uniformed.</li>
        <li><b>Transparent pricing</b> — flat hourly rates or per-service. No hidden fees.</li>
        <li><b>Live tracking</b> — know when your cleaner is on the way.</li>
        <li><b>100% guarantee</b> — re-clean free or full refund.</li>
        <li><b>Fair to cleaners</b> — same-day payouts, health cover, and skill upgrades.</li>
      </ul>
    </Section>

    <Section title="By the numbers">
      <div className="grid grid-cols-3 gap-3 mt-1">
        <StatBox n="10K+" label="Bookings" />
        <StatBox n="500+" label="Verified pros" />
        <StatBox n="4.9★" label="Avg rating" />
      </div>
    </Section>
  </PageShell>
);

const StatBox = ({ n, label }) => (
  <div className="bg-[#E8F5E9] rounded-xl p-4 text-center">
    <p className="text-2xl font-extrabold text-[#2E7D32]">{n}</p>
    <p className="text-xs text-neutral-600 mt-1">{label}</p>
  </div>
);
